import React from "react";

export interface DiffLine {
  type: "context" | "added" | "removed" | "hunk";
  content: string;
  oldLineNumber?: number;
  newLineNumber?: number;
}

interface DiffPreviewProps {
  filename: string;
  lines: DiffLine[];
}

const LINE_STYLES: Record<DiffLine["type"], string> = {
  context: "",
  added: "bg-green-500/10",
  removed: "bg-red-500/10",
  hunk: "bg-primary/5",
};

const PREFIX_STYLES: Record<DiffLine["type"], string> = {
  context: "text-surface-foreground/30",
  added: "text-green-600 dark:text-green-400",
  removed: "text-red-600 dark:text-red-400",
  hunk: "text-primary/60",
};

const LINE_PREFIXES: Record<DiffLine["type"], string> = {
  context: " ",
  added: "+",
  removed: "-",
  hunk: " ",
};

export function DiffPreview({
  filename,
  lines,
}: DiffPreviewProps): React.ReactElement {
  return (
    <div className="rounded-2xl border overflow-hidden">
      {/* Filename header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-surface/50 border-b">
        <span className="font-mono text-sm font-medium">{filename}</span>
      </div>

      {/* Diff lines */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse font-mono text-sm">
          <tbody>
            {lines.map((line, i) => (
              <tr key={i} className={LINE_STYLES[line.type]}>
                {line.type === "hunk" ? (
                  <td
                    colSpan={3}
                    className="px-4 py-0.5 text-xs text-primary/50 select-none"
                  >
                    {line.content}
                  </td>
                ) : (
                  <>
                    <td className="w-10 px-3 py-0.5 text-right text-xs text-surface-foreground/30 select-none border-r border-border/40 tabular-nums">
                      {line.oldLineNumber ?? ""}
                    </td>
                    <td className="w-10 px-3 py-0.5 text-right text-xs text-surface-foreground/30 select-none border-r border-border/40 tabular-nums">
                      {line.newLineNumber ?? ""}
                    </td>
                    <td className="px-4 py-0.5 whitespace-pre">
                      <span className={`mr-2 select-none ${PREFIX_STYLES[line.type]}`}>
                        {LINE_PREFIXES[line.type]}
                      </span>
                      <span className={line.type !== "context" ? PREFIX_STYLES[line.type] : ""}>
                        {line.content}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
